import React, { ChangeEvent } from 'react';
import {
  CJField,
  CJFormBlock,
  MultiSwitchInput,
  SelectInput,
  TextInput,
} from '@cjdev-internal/visual-stack-x/CJForm';
import intl from '../../../utils/intl';
import { content as tagContent } from '../../AdvertiserTagView';
import { content as messageContent } from '../../PartnershipPage/message';
import {
  keyField,
  partnerField,
  PartnerRowProps,
  statusField,
  DraftPartnerRow,
} from '../partnershipPageTypes';
import {
  generateUpdatedPartnerRowData,
  getPartnerOptions,
  getPartnershipModeOptions,
} from './PartnerRowHelper';
import { Partner } from '@callback-cats/partnerships-ts/Partnership';
import { isProperty } from '@callback-cats/ts-helpers';

export const PartnerRow = (props: PartnerRowProps) => {
  const rowIndex = parseInt(props.rowId);
  const availablePartners = getPartnerOptions(props.draftPartnerRows, rowIndex);

  if (props.config.getValues()[`${statusField}${props.rowId}`] === undefined) {
    props.config.setValue(`${statusField}${props.rowId}`, 'OFF');
  }

  function handlePartnerChange(event: unknown) {
    const newValue = event as ChangeEvent<HTMLSelectElement>;
    const newPartnerRows = generateUpdatedPartnerRowData(
      newValue.target.value,
      props.draftPartnerRows,
      props.rowId
    );

    props.config.setValue(`${keyField}${props.rowId}`, '');
    Object.keys(
      props.draftPartnerRows[rowIndex].partnership?.data || []
    ).forEach((fieldName) => {
      if (fieldName !== 'key') {
        props.config.setValue(`${fieldName}${props.rowId}`, '');
      }
    });
    props.setPartnerRows([...newPartnerRows]);
  }

  // Renders the key input
  function renderKeyInput(
    partnership: Partner | undefined,
    rowId: string,
    disabled: boolean = false
  ) {
    const label = partnership
      ? partnership.name === 'Sovendus'
        ? intl.formatMessage(messageContent.trafficSourceNumber)
        : intl.formatMessage(tagContent.partnerTableHeaderKey)
      : 'Key';
    const initialKey = partnership?.data.key ?? '';
    const currentKey = props.config.watch(`${keyField}${rowId}`);
    const helpText =
      currentKey !== undefined && currentKey !== initialKey ? 'Changed' : '';

    const elemKey = (
      <CJField
        columnSpan={1}
        required
        name={`${keyField}${rowId}`}
        label={label}
        help={helpText}
        disabled={disabled}
      >
        <TextInput required name={`${keyField}${rowId}`} />
      </CJField>
    );
    const data = partnership?.data || { key: '' };
    const elemList = Object.keys(data || []).map((key) => {
      const partnerData: keyof typeof data = isProperty(key, data)
        ? key
        : 'key';
      if (partnerData !== 'key') {
        return (
          <CJField
            columnSpan={1}
            required
            name={`${partnerData}${rowId}`}
            label={
              isProperty(partnerData, messageContent)
                ? intl.formatMessage(messageContent[partnerData])
                : ''
            }
            help={
              props.config.watch(`${key}${rowId}`) !== data[partnerData]
                ? 'Changed'
                : ''
            }
            disabled={disabled}
          >
            <TextInput required name={`${partnerData}${rowId}`} />
          </CJField>
        );
      } else return <></>;
    });
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: '10px',
        }}
      >
        {elemKey}
        {elemList}
      </div>
    );
  }

  // Main function to select which input to render
  const renderTextInputByPartner = (partnerRows: DraftPartnerRow[]) => {
    const rowIndex = parseInt(props.rowId);
    const currentPartner = partnerRows[rowIndex];
    if (!currentPartner) return null;
    const partnership = currentPartner.partnership;
    return renderKeyInput(partnership, props.rowId);
  };

  // Render the PartnerRow component
  return (
    <CJFormBlock columnCount={3}>
      <CJField
        columnSpan={1}
        required
        name={`${partnerField}${props.rowId}`}
        label={intl.formatMessage(tagContent.partnerTableHeaderName)}
        help={(() => {
          const initial = props.existingRows[parseInt(props.rowId)]?.name;
          const current = props.config.watch(`${partnerField}${props.rowId}`);
          return current !== undefined && current !== initial ? 'Changed' : '';
        })()}
      >
        <SelectInput
          required
          readOnly={parseInt(props.rowId) < props.existingRows.length}
          unpack
          name={`${partnerField}${props.rowId}`}
          placeholder="Partner"
          options={availablePartners}
          onChange={handlePartnerChange}
        />
      </CJField>

      {renderTextInputByPartner(props.draftPartnerRows)}

      <CJField
        columnSpan={1}
        required
        name={`${statusField}${props.rowId}`}
        label={intl.formatMessage(tagContent.partnerTableHeaderMode)}
        help={(() => {
          const initial = props.existingRows[parseInt(props.rowId)]?.mode;
          const current = props.config.watch(`${statusField}${props.rowId}`);
          return current !== undefined && current !== initial ? 'Changed' : '';
        })()}
      >
        <MultiSwitchInput
          required
          name={`${statusField}${props.rowId}`}
          options={getPartnershipModeOptions()}
          optionWidth="70px"
        />
      </CJField>
    </CJFormBlock>
  );
};
